import { clamp } from './utils.js'
import { trackToolUsage } from '../analytics.js'

export const interactionMethods = {
  handleAction(action) {
    if (['home', 'main', 'about', 'feedback'].includes(action)) {
      const section = action === 'home' ? 'main' : action
      document.body.classList.toggle('show-about', section === 'about')
      document.body.classList.toggle('show-feedback', section === 'feedback')
      document.querySelectorAll('nav .nav-link[data-action]').forEach(link =>
        link.classList.toggle('active', link.getAttribute('data-action') === section))
      return
    }
    if (!this.pdf && !['reset'].includes(action)) return
    trackToolUsage(action)

    if (action === 'prev') this.currentIndex = clamp(this.currentIndex - 1, 0, this.visiblePages.length - 1)
    if (action === 'next') this.currentIndex = clamp(this.currentIndex + 1, 0, this.visiblePages.length - 1)
    if (action === 'zoom-out') {
      this.autoZoom = false
      this.zoom = clamp(Number((this.zoom - 0.1).toFixed(1)), 0.5, 2.5)
    }
    if (action === 'zoom-in') {
      this.autoZoom = false
      this.zoom = clamp(Number((this.zoom + 0.1).toFixed(1)), 0.5, 2.5)
    }
    if (action === 'rotate-left') this.rotateCurrent(-90)
    if (action === 'rotate-right') this.rotateCurrent(90)
    if (action === 'delete') {
      if (!this.deleteSelectedText()) this.deleteCurrent()
    }
    if (action === 'select-mode') this.setSelectMode(true)
    if (action === 'text-mode') this.toggleTextMode()
    if (action === 'insert-tick') this.startTextPreset('✓', 'Tick', false)
    if (action === 'insert-cross') this.startTextPreset('✕', 'Cross', false)
    if (action.startsWith('markup-') && ['markup-highlight', 'markup-freehand', 'markup-arrow'].includes(action)) this.setMarkupMode(action.replace('markup-', ''))
    if (action === 'markup-duplicate') this.duplicateSelectedMarkup()
    if (action === 'markup-rotate') this.rotateSelectedMarkup()
    if (action === 'markup-delete') this.deleteSelectedMarkup()
    if (action === 'signature') this.openSignaturePad()
    if (action === 'undo') this.undo()
    if (action === 'redo') this.redo()
    if (action === 'save') this.save()
    if (action === 'search-prev') this.stepSearch(-1)
    if (action === 'search-next') this.stepSearch(1)
    if (action === 'search-close') this.closeSearch()
    if (action === 'reset') this.reset()

    if (['prev', 'next', 'zoom-out', 'zoom-in'].includes(action)) this.renderCurrentPage()
  },

  toggleTextMode() {
    this.textPresetEditable = true
    this.markMode = false
    this.textInsertValue = 'Text'
    this.setTextMode(!this.textMode)
    this.setStatus(this.textMode ? 'Click the page to place text.' : 'Text tool off.')
  },

  startTextPreset(value, label, editable = true) {
    this.textPresetEditable = editable
    this.markMode = !editable
    this.textInsertValue = value
    this.setTextMode(true)
    this.updateTextInsertPreview()
    this.setStatus(`${label} tool active. Click the page to place it.`)
  },

  setTextMode(enabled) {
    this.textMode = enabled
    this.setSelectMode(!enabled)
    document.querySelector('.text-mode').classList.toggle('active', this.textMode)
    document.querySelector('.text-mode').setAttribute('aria-pressed', String(this.textMode))
    this.stage.classList.toggle('adding-text', this.textMode)
    this.stage.classList.toggle('adding-mark', this.textMode && this.markMode)
    document.body.classList.toggle('text-tool-active', this.textMode)
    if (!this.textMode) {
      this.markMode = false
      this.hideTextInsertPreview()
    }
  },

  setSelectMode(enabled) {
    this.selectMode = enabled
    const button = document.querySelector('.select-mode')
    button?.classList.toggle('active', enabled)
    button?.setAttribute('aria-pressed', String(enabled))
    this.stage.classList.toggle('selecting', enabled)
    if (enabled && this.markupMode) {
      this.markupMode = null
      document.querySelectorAll('.markup-mode').forEach(button => button.classList.remove('active'))
      this.stage.classList.remove('drawing-markup')
    }
    if (enabled && this.textMode) {
      this.textMode = false
      this.markMode = false
      document.querySelector('.text-mode')?.classList.remove('active')
      document.querySelector('.text-mode')?.setAttribute('aria-pressed', 'false')
      document.body.classList.remove('text-tool-active')
      this.stage.classList.remove('adding-text', 'adding-mark')
      this.hideTextInsertPreview()
      this.setStatus('Select tool active. Click text to edit it.')
    }
  },

  setEditTextMode(enabled) {
    this.editTextMode = enabled
    this.stage.classList.toggle('editing-text', this.editTextMode)
  },

  exitCurrentEdit() {
    if (this.editingAnnotationId) {
      this.exitAnnotationEdit()
      return
    }
    if (this.editingTextItemId) {
      this.exitTextEdit()
      return
    }
    if (this.selectedAnnotationId) {
      this.deselectAnnotation()
      return
    }
    if (this.selectedTextItem) {
      this.deselectPdfText()
      return
    }
    if (this.selectedMarkupId) {
      this.selectedMarkupId = null
      this.renderVisualAnnotations(this.visiblePages[this.currentIndex])
      this.syncMarkupOptions()
      this.updateDeleteControl()
      return
    }
    if (this.markupMode) {
      this.setMarkupMode(this.markupMode)
      return
    }
    if (this.textMode) {
      this.setTextMode(false)
      this.setStatus('Text tool off.')
    }
  },

  shouldHandleDeleteKey(event) {
    if (!this.selectedAnnotationId && !this.selectedTextItem && !this.selectedMarkupId) return false
    const target = event.target
    if (!target) return true
    if (target.closest?.('textarea, input, select, [contenteditable="true"]')) return false
    return true
  },

  deleteSelectedText() {
    if (this.selectedMarkupId) return this.deleteSelectedMarkup()
    if (this.selectedAnnotationId) return this.removeAnnotation(this.selectedAnnotationId)
    if (this.selectedTextItem) return this.removePdfText(this.selectedTextItem)
    return false
  },

  handleStageClick(event) {
    if (this.editingTextItemId && !event.target.closest('.editable-text')) {
      this.exitTextEdit()
      return
    }
    if (event.target.closest('.annotation-tool-panel')) return
    if (this.editingAnnotationId && !event.target.closest('.annotation-note')) {
      this.exitAnnotationEdit()
      return
    }
    if (this.selectedTextItem && !event.target.closest('.editable-text')) this.deselectPdfText()
    if (this.selectedAnnotationId && !event.target.closest('.annotation-note')) this.deselectAnnotation()
    if (this.selectedMarkupId && !event.target.closest('.markup-object')) {
      this.selectedMarkupId = null
      this.renderVisualAnnotations(this.visiblePages[this.currentIndex])
      this.syncMarkupOptions()
    }
    const pageState = this.visiblePages[this.currentIndex]
    if (!this.textMode || !pageState) return
    const rect = this.annotationLayer.getBoundingClientRect()
    const x = clamp((event.clientX - rect.left) / rect.width, 0, 1)
    const y = clamp((event.clientY - rect.top) / rect.height, 0, 1)
    const text = this.textPresetEditable ? '' : this.textInsertValue
    if (!this.textPresetEditable && !text) return
    const matchedStyle = this.insertedTextStyleFor(pageState, x, y, 24)

    const annotation = {
      id: this.createAnnotationId(pageState),
      x,
      y,
      text,
      size: matchedStyle.size,
      color: '#333333',
      fontFamily: matchedStyle.fontFamily,
      editable: this.textPresetEditable,
    }
    this.recordHistory()
    pageState.annotations.push(annotation)
    this.selectedAnnotationId = this.textPresetEditable ? annotation.id : null
    this.editingAnnotationId = this.textPresetEditable ? annotation.id : null
    this.focusAnnotationEditor = this.textPresetEditable
    this.updateDirtyState()
    this.renderCurrentPage()
    this.setStatus(this.textPresetEditable
      ? 'Text added. Press Escape to finish editing, then Escape again to leave insert mode.'
      : 'Mark added. Press Escape to leave insert mode.')
  },

  reset() {
    this.clearSourceFontFaces()
    this.sourceBytes = null
    this.pdf = null
    this.sourcePasswordProtected = false
    for (const document of this.documents.values()) document.pdf?.destroy?.().catch?.(() => {})
    this.documents.clear()
    this.clearSearch()
    this.pages = []
    this.currentIndex = 0
    this.zoom = 1
    this.autoZoom = true
    this.textMode = false
    this.markMode = false
    this.selectMode = false
    this.editTextMode = false
    this.selectedTextItem = null
    this.selectedAnnotationId = null
    this.selectedMarkupId = null
    this.markupMode = null
    this.markupInteraction = null
    this.editingTextItemId = null
    this.editingAnnotationId = null
    this.focusInPlaceEditor = false
    this.focusAnnotationEditor = false
    this.textPresetEditable = true
    this.textInsertValue = ''
    this.annotationDrag = null
    this.savedState = ''
    this.clearHistory()
    clearTimeout(this.panelEditRenderTimer)
    clearTimeout(this.textStyleRenderTimer)
    clearTimeout(this.resizeTimer)
    this.canvas.width = 0
    this.canvas.height = 0
    this.pageList.innerHTML = ''
    this.textEditLayer.innerHTML = ''
    this.annotationLayer.innerHTML = ''
    this.markupLayer.innerHTML = ''
    this.resetTextEditor()
    this.stage.classList.remove('adding-mark')
    this.stage.classList.remove('drawing-markup')
    this.markupOptions.hidden = true
    this.syncAnnotationTools()
    document.body.classList.remove('not-empty', 'show-about', 'text-tool-active')
    document.querySelector('.text-mode').classList.remove('active')
    document.querySelector('.text-mode').setAttribute('aria-pressed', 'false')
    document.querySelector('.select-mode')?.classList.remove('active')
    document.querySelector('.select-mode')?.setAttribute('aria-pressed', 'false')
    document.querySelectorAll('.markup-mode').forEach(button => button.classList.remove('active'))
    document.querySelectorAll<HTMLInputElement>('.inputfile').forEach(input => { input.value = '' })
    this.enableControls(false)
    this.updateCounters()
    this.setStatus('No PDF loaded.')
  },

  enableControls(enabled) {
    const resetButton = document.querySelector('[data-action="reset"]')
    if (resetButton instanceof HTMLButtonElement) resetButton.disabled = !enabled
    this.enablePageActions(enabled)
    if (this.imageInput) this.imageInput.disabled = !enabled
    this.updateCounters()
    this.updateDirtyState()
  },

  setStatus(message) {
    this.status.textContent = message
  },

  serializeState() {
    return JSON.stringify(this.pages.map(page => ({
      id: page.id,
      documentId: page.documentId,
      sourceIndex: page.sourceIndex,
      rotation: page.rotation,
      deleted: page.deleted,
      annotations: page.annotations,
      markups: page.markups || [],
      textEdits: Object.fromEntries(Object.entries(page.textEdits).sort(([a], [b]) => a.localeCompare(b))),
    })))
  },

  isDirty() {
    return Boolean(this.pdf) && this.serializeState() !== this.savedState
  },

  updateDirtyState() {
    const saveButton = document.querySelector('[data-action="save"]')
    const resetButton = document.querySelector('[data-action="reset"]')
    this.setToolbarDisabled(saveButton, !this.pdf || !this.visiblePages.length)
    if (resetButton instanceof HTMLButtonElement) resetButton.disabled = !this.pdf
    this.updateDeleteControl()
    this.updateHistoryControls()
  },

  updateDeleteControl() {
    const deleteButton = document.querySelector('[data-action="delete"]')
    if (!deleteButton) return
    const hasMarkupSelection = Boolean(this.selectedMarkupId)
    const hasTextSelection = Boolean(this.selectedAnnotationId || this.selectedTextItem)
    const label = hasMarkupSelection ? 'Delete selected visual object' : hasTextSelection ? 'Delete selected text' : 'Delete page'
    this.setToolbarTooltip(deleteButton, label)
    deleteButton.setAttribute('aria-label', label)
  }
}
